import React from 'react';
import { useMediaQuery } from '@mui/material';
import classNames from 'classnames';
import CustomMuiDropdown from '../../../CustomMuiDropdown';
import useSidePanel from '../../../SidePanel/hooks/useSidePanel';
import { getGradebook } from '../../../../actions/gradebook';
import { CLOSE_SIDE_PANEL } from '../../../SidePanel/store/actions';
import { GRADEBOOK_SORT } from '../../../../utils/constants';
import { getConfigObject } from '../../../../utils/config';
import { useGradebookPageSizeOptional } from '../../InstructorGradebookV2/context/GradebookPageSizeContext';
import {
  PAGE_SIZE_OPTIONS,
  DEFAULT_PAGE_SIZE,
} from '../../InstructorGradebookV2/components/Gradebook/constants';

import s from './ShowRowsMenu.module.css';

/**
 * Build sort parameters for the API based on column type
 * Converts the sortColumn identifier to the appropriate API parameters
 */
const buildSortParams = (sortColumn, sortDirection) => {
  const direction = sortDirection || 'asc';

  // Default/name sort
  if (
    !sortColumn ||
    sortColumn === 'name' ||
    sortColumn === GRADEBOOK_SORT.NAME_SORT_COLUMN
  ) {
    return { sortBy: 'name', sortDirection: direction };
  }

  // Course total sort
  if (sortColumn === 'courseTotal') {
    return { sortBy: 'course_total', sortDirection: direction };
  }

  // Category total sort: column is 'category-total-{id}'
  if (sortColumn.startsWith('category-total-')) {
    const categoryId = sortColumn.replace('category-total-', '');
    return {
      sortByCategory: parseInt(categoryId, 10),
      sortDirection: direction,
    };
  }

  // Assignment sort: column is the assignment ID
  return {
    sortByAssignment: sortColumn,
    sortDirection: direction,
  };
};

function ShowRowsMenu({
  dispatch,
  courseId,
  limit,
  count,
  searchPhrase,
  sortColumn,
  sortDirection,
  exceptionGroups,
  filterTypes,
  isGradebookV2Active,
  getGradebookInstructor,
}) {
  const { state: sidePanelState, dispatch: sidePanelDispatch } = useSidePanel();
  const { mediaQueries } = sidePanelState;
  const isMobile = useMediaQuery(`(max-width: ${mediaQueries.mobileOnly}px)`);
  const { FF_IS_INSTRUCTOR_GRADEBOOK_SHOW_ALL_ENABLED = false } =
    getConfigObject();
  const pageSizeContext = useGradebookPageSizeOptional();
  const setSessionPageSize = pageSizeContext?.setPageSize;
  // V2: Redux limit reflects the last successful fetch; use it for display.
  const displayedPageSize = limit;

  // When sorted by a non-default column (not by name), V1 requires showing all students
  const isSorted =
    sortColumn &&
    sortColumn !== 'name' &&
    sortColumn !== GRADEBOOK_SORT.NAME_SORT_COLUMN;
  // Show "Show All" when:
  // - V1: Sorting by grade (isSorted) requires showing all students for client-side sort
  // - V2: Server-side sorting supports pagination, so only show "Show All" when limit matches count
  // - Both: User explicitly selected "Show All" (limit matches count, not a standard option, and count > 0)
  const isShowingAll = isGradebookV2Active
    ? !PAGE_SIZE_OPTIONS.includes(displayedPageSize) &&
      displayedPageSize >= count &&
      count > 0
    : isSorted ||
      (!PAGE_SIZE_OPTIONS.includes(limit) && limit >= count && count > 0);
  // If limit doesn't match any option, use the default
  const effectiveLimit = PAGE_SIZE_OPTIONS.includes(displayedPageSize)
    ? displayedPageSize
    : DEFAULT_PAGE_SIZE;

  const handleSelect = (item) => {
    const value = item.value;
    const newLimit = value === -1 ? count : value;
    if (isGradebookV2Active && setSessionPageSize) {
      setSessionPageSize(newLimit);
    }
    if (isGradebookV2Active && getGradebookInstructor) {
      // Build sort parameters based on current sort column
      const sortParams = buildSortParams(sortColumn, sortDirection);

      // getGradebookInstructor is already dispatched by the container
      getGradebookInstructor(courseId, {
        page: 1,
        pageSize: newLimit,
        search: searchPhrase,
        ...sortParams,
        exceptionGroupIds: (exceptionGroups || []).map((g) => g.groupId),
        filterTypes: filterTypes || [],
      });
    } else {
      dispatch(
        getGradebook(courseId, {
          page: 1,
          limit: newLimit,
          search: searchPhrase,
        }),
      );
    }
    if (sidePanelState.open) {
      sidePanelDispatch({ type: CLOSE_SIDE_PANEL });
    }
  };

  // Build items for CustomMuiDropdown
  const items = [
    ...PAGE_SIZE_OPTIONS.map((size) => ({
      label: `Show ${size}`,
      value: size,
    })),
    {
      label: 'Show All',
      value: -1,
      hide: !FF_IS_INSTRUCTOR_GRADEBOOK_SHOW_ALL_ENABLED,
    },
  ];

  // Determine selected value
  const selectedValue = isShowingAll ? -1 : effectiveLimit;

  return (
    <CustomMuiDropdown
      items={items}
      selectedValue={selectedValue}
      onClickItem={handleSelect}
      className={classNames(s.menuButton, { [s.menuButtonMedium]: isMobile })}
      labelClassName={classNames({ [s.buttonLabelMedium]: isMobile })}
      dataTestId="gradebook-show-rows-button"
      ariaLabel="Select number of rows to show"
    />
  );
}

export default ShowRowsMenu;
